'use client';

import './preference-row.scss';

import type { PropsWithChildren, ReactNode } from 'react';

import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Checkbox } from '@sola/ark-core/molecules/checkbox/checkbox.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';

export type PreferenceOption = {
  id: string;
  label: string;
  value: string;
  defaultChecked?: boolean;
  disabled?: boolean;
  disabledTooltip?: ReactNode;
};

export type PreferenceRowProps = {
  fieldName: string;
  title: string;
  description?: string;
  options: PreferenceOption[];
  className?: string;
};

export const PreferenceSection: React.FC<
  PropsWithChildren<{ title?: string }>
> = ({ title, children }) => {
  return (
    <div className="flex flex-col max-w-md gap-comfy-xl">
      {title && (
        <h2 className="label-neutral-amplified label-potent label-xs py-comfy-lg">
          {title}
        </h2>
      )}
      {children}
    </div>
  );
};

export const PreferenceRowContainer: React.FC<
  PropsWithChildren<{ className?: string }>
> = ({ className, children }) => {
  return (
    <div
      className={cn(
        'PreferenceRow flex flex-col md:flex-row items-start md:justify-between pb-comfy-lg',
        className,
      )}
    >
      {children}
    </div>
  );
};

export const PreferenceRow: React.FC<PreferenceRowProps> = ({
  fieldName,
  title,
  description,
  options,
  className,
}) => {
  return (
    <PreferenceRowContainer className={className}>
      <div className="PreferenceRow__details flex flex-col gap-snug-md pb-comfy-sm">
        <h3 className="heading-xs heading-neutral-amplified">{title}</h3>
        {description && (
          <p className="body-sm body-neutral-mild">{description}</p>
        )}
      </div>
      <div className="px-comfy-xs">
        <fieldset
          className="PreferenceRow__options shrink-0"
          aria-label={title}
        >
          <legend className="sr-only">{title}</legend>
          <div className="flex flex-col gap-comfy-xs">
            {options.map((option) => (
              <Checkbox
                key={option.id}
                name={`${fieldName}.${option.value}`}
                label={option.label}
                defaultChecked={option.defaultChecked}
                labelContent={
                  <span className="label-moderate label-lg">
                    {option.label}
                  </span>
                }
                variant="tinted"
                disabled={option.disabled}
                disabledTooltip={option.disabledTooltip}
              />
            ))}
          </div>
        </fieldset>
      </div>
    </PreferenceRowContainer>
  );
};

export interface PreferenceRowSkeletonProps {
  className?: string;
  optionCount?: number;
}

export const PreferenceRowSkeleton: React.FC<PreferenceRowSkeletonProps> = ({
  className,
  optionCount = 2,
}) => (
  <PreferenceRowContainer className={className}>
    <div className="PreferenceRow__details flex flex-1 flex-col gap-snug-md pb-comfy-sm">
      <Skeleton height={24} width="55%" />
      <Skeleton height={16} count={2} />
    </div>
    <div className="flex min-w-40 flex-col gap-comfy-xs px-comfy-xs">
      {Array.from({ length: optionCount }, (_, index) => (
        <Skeleton key={index} height={24} />
      ))}
    </div>
  </PreferenceRowContainer>
);
